import React, { useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import { claimNeedsIdentity } from '../../hooks/pro/useMyClaim';

/**
 * "C'est mon commerce" — what a moderator needs to hand a listing over.
 *
 * The legal identity is asked for up front because it is what the moderator
 * checks the claim against, and what the listing lacks to stay published once
 * it has an owner. A private individual has none, and is not asked.
 *
 * Presentational: the request itself is `useMyClaim().send`, passed in as
 * `onSubmit`, so the wizard and the listing page send the same thing.
 */
function ClaimForm({ provider, onSubmit, onCancel, saving = false, error = '', fieldErrors = {} }) {
  const [values, setValues] = useState({ legalName: '', siret: '', message: '' });
  const asksIdentity = claimNeedsIdentity(provider);

  const change = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit(values);
  };

  return (
    <Box component="form" noValidate onSubmit={submit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        Un modérateur vérifie que vous représentez bien <strong>{provider.name}</strong> avant de
        vous confier la fiche. Elle reste en ligne pendant ce temps, et vous serez prévenu par
        email de la décision.
      </Typography>

      {asksIdentity && (
        <>
          <TextField
            label="Raison sociale"
            name="legalName"
            value={values.legalName}
            onChange={change}
            error={Boolean(fieldErrors.legalName)}
            helperText={fieldErrors.legalName || 'Le nom de l’entreprise sur ses documents'}
            required
            disabled={saving}
            inputProps={{ maxLength: 180 }}
          />

          <TextField
            label="SIRET"
            name="siret"
            value={values.siret}
            onChange={change}
            error={Boolean(fieldErrors.siret)}
            helperText={fieldErrors.siret || '14 chiffres, espaces autorisés'}
            required
            disabled={saving}
            inputProps={{ maxLength: 20 }}
          />
        </>
      )}

      <TextField
        label="Un mot pour le modérateur (facultatif)"
        name="message"
        value={values.message}
        onChange={change}
        error={Boolean(fieldErrors.message)}
        helperText={fieldErrors.message || 'Par exemple : « je tiens la boutique depuis 2019 ».'}
        multiline
        minRows={2}
        disabled={saving}
        inputProps={{ maxLength: 1000 }}
      />

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={saving}>
            Annuler
          </Button>
        )}
        <Button type="submit" variant="contained" disabled={saving}>
          Envoyer ma demande
        </Button>
      </Box>
    </Box>
  );
}

export default ClaimForm;
